import React, { memo, useCallback } from 'react';
import { StyleSheet } from 'react-native';

import { useNavigation } from '@react-navigation/native';

import { Button } from '@/components/ButtonComponent';
import { LoadingSkeleton } from '@/components/SkeletonLoader';
import { Text } from '@/components/TextComponent';
import { Box, Image, TouchableBox } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useSkiaImageCache } from '@/components/player-profile/hooks/use-skia-image-cache';
import { RafWithTheme } from '@/feature/invite-friends/hooks/use-raf';
import { common, designSystem } from '@/styles/styles';
import { isWeb } from '@/utils/constants-platform-specific';
import {
    BackdropBlur,
    BlurMask,
    Canvas,
    Group,
    RoundedRect,
    Image as SkiaImage,
    rect,
    rrect,
} from '@shopify/react-native-skia';

import { carouselItemHeight, itemSeparatorWidth } from './Carousel';
import { GlowShape } from './OvalGlow';

export const RAF_PROMO_CARD_WIDTH = 125;
const GLOW_SIZE = 215;
const GIFT_IMAGE_SIZE = 129;

const BUTTON_WIDTH = RAF_PROMO_CARD_WIDTH - 32;
const BUTTON_HEIGHT = 28;
const BUTTON_X = 16;
const BUTTON_Y = carouselItemHeight - 16 - BUTTON_HEIGHT;
const BUTTON_RADIUS = 8;

const BlurButton = () => {
    const buttonRRect = rrect(rect(BUTTON_X, BUTTON_Y, BUTTON_WIDTH, BUTTON_HEIGHT), BUTTON_RADIUS, BUTTON_RADIUS);
    const buttonBorderRRect = rrect(
        rect(BUTTON_X + 0.5, BUTTON_Y + 0.5, BUTTON_WIDTH - 1, BUTTON_HEIGHT - 1),
        BUTTON_RADIUS - 0.5,
        BUTTON_RADIUS - 0.5
    );

    return (
        <Group>
            {/* shadow */}
            <RoundedRect rect={buttonRRect} color={designSystem.colors.neutralBlack}>
                <BlurMask blur={20} style={'normal'} />
            </RoundedRect>
            {/* blur */}
            <Group clip={buttonRRect}>
                <BackdropBlur blur={4} />
                <BackdropBlur blur={4} />
            </Group>
            {/**one off hardcoded color - primitive purple[300], as discussed with the UX team */}
            <RoundedRect rect={buttonRRect} color={'#D8B4FE'} opacity={0.3} />
            {/* border */}
            <RoundedRect
                rect={buttonBorderRRect}
                style={'stroke'}
                strokeWidth={1}
                color={ds.colorValue.border.strong}
            />
        </Group>
    );
};

export const RafPromoCard = memo(({ isLoading, rafConfig }: { isLoading: boolean; rafConfig?: RafWithTheme }) => {
    const navigation = useNavigation();

    const navigateToRaf = useCallback(() => {
        navigation.navigate('InviteFriends');
    }, [navigation]);

    const giftImage = useSkiaImageCache({
        url: isWeb ? undefined : rafConfig?.data.raf_promo_card_image?.url ?? undefined,
    });

    if (isLoading) {
        return <RafPromoCardSkeleton />;
    }

    const rafPromoCardLabel = rafConfig?.data?.raf_promo_card_label || 'EARN $10+';

    return (
        <TouchableBox
            onPress={navigateToRaf}
            activeOpacity={0.7}
            width={RAF_PROMO_CARD_WIDTH}
            flex={1}
            backgroundColor={ds.colorToken.background.neutral.subtler}
            style={[common.borderCurve, styles.rafPromoCard]}
            borderRadius={'r20'}
            paddingVertical="s16"
            alignItems={'center'}
            justifyContent={'space-between'}
            overflow={'hidden'}
        >
            <Box style={styles.border} />

            <Box style={StyleSheet.absoluteFill} position={'absolute'} zIndex={-1}>
                <GlowShape
                    opacity={1}
                    color={ds.colorValue.background.brand.emphasis}
                    shape="oval"
                    x={RAF_PROMO_CARD_WIDTH / 2 - GLOW_SIZE / 2}
                    y={84}
                    height={GLOW_SIZE}
                    width={GLOW_SIZE}
                    blur={56}
                />
            </Box>

            {isWeb ? (
                <Box position={'absolute'} top={40}>
                    {rafConfig?.data.raf_promo_card_image?.url ? (
                        <Image
                            style={styles.rafPromoCardImage}
                            source={{ uri: rafConfig.data.raf_promo_card_image.url }}
                            resizeMode="contain"
                        />
                    ) : null}
                </Box>
            ) : (
                <Canvas style={styles.canvas} pointerEvents={'none'}>
                    {giftImage ? (
                        <SkiaImage
                            image={giftImage}
                            x={(RAF_PROMO_CARD_WIDTH - GIFT_IMAGE_SIZE) / 2}
                            y={40}
                            width={GIFT_IMAGE_SIZE}
                            height={GIFT_IMAGE_SIZE}
                            fit={'contain'}
                        />
                    ) : null}
                    <BlurButton />
                </Canvas>
            )}

            <Text
                fontFamily={'Gamay'}
                fontWeight={800}
                fontSize={18}
                lineHeight={22}
                letterSpacing={-0.54}
                textTransform={'uppercase'}
            >
                {rafPromoCardLabel}
            </Text>
            {isWeb ? (
                <Button
                    label={'Send invites'}
                    size="xs"
                    hierarchy="primary"
                    disabled={true}
                    style={styles.sendInvitesButton}
                    textStyle={styles.sendInvitesButtonText}
                />
            ) : (
                <Box width={BUTTON_WIDTH} height={BUTTON_HEIGHT} justifyContent={'center'} alignItems={'center'}>
                    <Text variant={'labelMedium'} fontWeight={'600'} lineHeight={20} color={ds.colorToken.text.primary}>
                        Send invites
                    </Text>
                </Box>
            )}
        </TouchableBox>
    );
});

const RafPromoCardSkeleton = memo(() => {
    return (
        <Box
            width={RAF_PROMO_CARD_WIDTH}
            height={carouselItemHeight}
            borderRadius={'r20'}
            backgroundColor={ds.colorToken.background.neutral.subtler}
            style={styles.rafPromoCard}
        >
            <LoadingSkeleton
                width={RAF_PROMO_CARD_WIDTH}
                height={carouselItemHeight}
                borderRadius="r20"
                skeletonColor={designSystem.colors.neutral300}
            />
        </Box>
    );
});

const styles = StyleSheet.create({
    border: {
        ...StyleSheet.absoluteFillObject,
        zIndex: 1,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: ds.colorValue.border.subtle,
        pointerEvents: 'none',
    },
    canvas: {
        ...StyleSheet.absoluteFillObject,
    },
    rafPromoCard: {
        marginRight: itemSeparatorWidth,
    },
    rafPromoCardImage: {
        width: GIFT_IMAGE_SIZE,
        height: GIFT_IMAGE_SIZE,
    },
    sendInvitesButton: {
        backgroundColor: ds.colorValue.background.neutral.inverse,
    },
    sendInvitesButtonText: {
        color: designSystem.colors.neutral800,
    },
});
